import React, { useState, useEffect } from 'react';
import { Brain, ChevronDown, ChevronUp, Clock, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import AIRecommendationBadge from './AIRecommendationBadge';
import api, { AIAnalysisHistoryItem } from '../services/api';

interface AIAnalysisHistoryProps {
  symbol: string;
}

const AIAnalysisHistory: React.FC<AIAnalysisHistoryProps> = ({ symbol }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [history, setHistory] = useState<AIAnalysisHistoryItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, [symbol]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getAIAnalysisHistory(symbol, 10);
      setHistory(data);
    } catch (err) {
      console.error('Failed to load AI analysis history:', err);
      setError('No AI analysis history available');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else if (diffDays < 30) {
      return `${Math.floor(diffDays / 7)}w ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getRecommendationTrend = () => {
    if (history.length < 2) return null;

    const recommendationValues = {
      strong_sell: 1,
      sell: 2,
      hold: 3,
      buy: 4,
      strong_buy: 5,
    };

    const latest = recommendationValues[history[0].recommendation];
    const previous = recommendationValues[history[1].recommendation];

    if (latest > previous) {
      return { direction: 'up', text: 'Improving' };
    } else if (latest < previous) {
      return { direction: 'down', text: 'Declining' };
    }
    return { direction: 'stable', text: 'Stable' };
  };

  if (loading) {
    return (
      <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner size="sm" />
        </div>
      </div>
    );
  }

  if (error || history.length === 0) {
    return null; // Don't show the section if there's no history
  }

  const trend = getRecommendationTrend();

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-4"
      >
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-2">
          <Brain size={16} className="text-purple-400" />
          AI Analysis History
          {trend && (
            <span className={`text-xs font-normal ml-2 ${
              trend.direction === 'up' ? 'text-emerald-400' :
              trend.direction === 'down' ? 'text-red-400' :
              'text-slate-400'
            }`}>
              ({trend.text})
            </span>
          )}
        </h3>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && (
        <div className="space-y-3">
          {history.map((item, index) => (
            <div
              key={item.id}
              className={`flex items-start justify-between gap-3 p-3 rounded-lg ${
                index === 0
                  ? 'bg-purple-500/10 border border-purple-500/20'
                  : 'bg-slate-700/30'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <AIRecommendationBadge
                    recommendation={item.recommendation}
                    confidence={item.confidence}
                    size="sm"
                    showIcon={false}
                  />
                  {index === 0 && (
                    <span className="text-xs font-medium text-purple-400">Latest</span>
                  )}
                </div>
                {item.summary && (
                  <p className="text-xs text-slate-400 line-clamp-2 mt-1">
                    {item.summary}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                <Clock size={12} />
                <span>{formatDate(item.created_at)}</span>
              </div>
            </div>
          ))}

          {history.length > 0 && (
            <div className="pt-2 text-center">
              <button
                onClick={() => navigate(`/ai-history?symbol=${symbol}`)}
                className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
              >
                View full analysis details →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAnalysisHistory;
